import Dexie from 'dexie';

/**
 * v7.15 — Detecção de IndexedDB no boot (iOS/Safari Private Mode).
 *
 * Em modo privado do Safari iOS, `window.indexedDB` existe mas QUALQUER
 * operação (`open`, `add`, etc.) lança `SecurityError` ou `QuotaExceededError`.
 * Sem essa detecção, o seedDatabase trava o boot na splash.
 *
 * Quando `indexedDBAvailable === false`, App.js mostra fallback amigável
 * em vez de tela em branco.
 */
export const indexedDBAvailable = (() => {
  try {
    if (typeof window === 'undefined') return false;
    if (!window.indexedDB) return false;
    // Quick probe: tenta abrir um DB temporário e fechar.
    // Em modo privado iOS, `open` retorna mas `onerror` dispara.
    // Aqui só validamos que o objeto está presente. O teste real
    // é capturado no try/catch do seedDatabase.
    return true;
  } catch {
    return false;
  }
})();

/**
 * v7.45 — Gera um ID único e estável para esta instalação isolar seu
 * namespace de sync no Firebase RTDB.
 *
 * Formato: `kaven-{16 chars hex}` (8 bytes de entropia via CSPRNG).
 * Colisão é astronomicamente improvável (2^64 ≈ 1.8 × 10^19 possibilidades),
 * dispensa coordenação central.
 *
 * Chamado APENAS pelo seedDatabase, e APENAS quando `deviceGroup` está
 * ausente ou tem o valor legado "kaven-default". O ID gerado é persistido
 * no Dexie UMA VEZ — boots futuros encontram o valor existente e a
 * checagem idempotente do seed garante que ele nunca é regerado.
 *
 * Por que não usar `crypto.randomUUID()`? Disponível em browsers modernos
 * mas o formato com hífens (`xxxxxxxx-xxxx-...`) é menos compacto. Manter
 * o prefixo "kaven-" facilita debug no console do Firebase RTDB.
 */
function generateUniqueDeviceGroup() {
  const buf = new Uint8Array(8);
  // crypto está sempre disponível em browsers (window.crypto / worker.crypto)
  // e é o método recomendado pelo W3C Web Crypto API spec.
  crypto.getRandomValues(buf);
  const hex = Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
  return `kaven-${hex}`;
}


const db = new Dexie('KavenLocalDB');

// v7.47 — Flag de shutdown intencional. Quando true (durante resetDatabase
// ou versionchange), polls e writes NÃO devem tentar reabrir o DB. Sem
// isso, `useVisiblePolling` cairia em loop de DatabaseClosedError ↔ open()
// brigando com o tear-down em curso.
let _isShuttingDown = false;

export function isDbShuttingDown() {
  return _isShuttingDown;
}

/**
 * v7.83 (ticket 218940) — Supressor compartilhado de DatabaseClosedError.
 *
 * Enquanto o DB está fechado (upgrade bloqueado por outra aba, tear-down em
 * curso), TODA chamada Dexie pendente rejeita com `DatabaseClosedError`. Sem
 * absorver isso vira enxurrada de unhandledrejection — e o overlay do
 * webpack-dev-server transforma a tela num paredão vermelho. Contado por
 * referência: só remove o listener quando o último interessado libera.
 *
 * @returns {() => void} funcao de release (idempotente)
 */
let _suppressorRefs = 0;
let _suppressorHandler = null;
function suppressDbClosedErrors() {
  if (typeof window === 'undefined') return () => {};
  if (_suppressorRefs === 0) {
    _suppressorHandler = (ev) => {
      const msg = ev?.reason?.message || String(ev?.reason || '');
      if (/DatabaseClosed|DexieError|MissingAPIError/i.test(msg)) ev.preventDefault?.();
    };
    window.addEventListener('unhandledrejection', _suppressorHandler, { capture: true });
  }
  _suppressorRefs += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    _suppressorRefs = Math.max(0, _suppressorRefs - 1);
    if (_suppressorRefs === 0 && _suppressorHandler) {
      try { window.removeEventListener('unhandledrejection', _suppressorHandler, { capture: true }); } catch {}
      _suppressorHandler = null;
    }
  };
}

/**
 * v7.83 (ticket 218940) — Canal de coordenação entre abas do MESMO origin.
 *
 * O restaurante opera com KDS + delivery + entregador + admin abertos ao mesmo
 * tempo. Num publish que sobe a versão do schema, a aba que tenta o upgrade
 * recebe `blocked` enquanto qualquer outra mantém a versão antiga aberta. Em
 * vez de pedir pro operador sair caçando abas no meio do serviço, a aba
 * bloqueada avisa as irmãs pra liberarem o DB — o upgrade conclui sozinho.
 */
const DB_CHANNEL_NAME = 'kaven-db-coordination';
let _dbChannel = null;
function getDbChannel() {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return null;
  if (!_dbChannel) {
    try { _dbChannel = new BroadcastChannel(DB_CHANNEL_NAME); } catch { return null; }
  }
  return _dbChannel;
}

/**
 * v7.83 (ticket 218940) — Reabre o DB insistindo, com teto de tempo.
 *
 * Um `db.open()` que pegou `blocked` fica PENDENTE (não rejeita) e resolve
 * sozinho quando as outras conexões fecham; chamadas repetidas devolvem a
 * mesma promise. Por isso corremos contra um timeout curto: se não abriu no
 * intervalo, re-avisamos as irmãs e tentamos de novo.
 *
 * @param {{ announce?: boolean, budgetMs?: number }} opts
 * @returns {Promise<boolean>} true se o DB abriu dentro do orçamento
 */
async function reopenWithRetry({ announce = false, budgetMs = 120000 } = {}) {
  const deadline = Date.now() + budgetMs;
  for (let attempt = 0; Date.now() < deadline; attempt += 1) {
    if (announce) {
      try { getDbChannel()?.postMessage({ type: 'release-db', at: Date.now() }); } catch {}
    }
    try {
      if (db.isOpen()) return true;
      const opened = await Promise.race([
        db.open().then(() => true),
        new Promise((resolve) => setTimeout(() => resolve(false), attempt < 4 ? 1200 : 4000)),
      ]);
      if (opened || db.isOpen()) return true;
    } catch {
      // VersionError (bundle antigo desta aba) ou erro real de open: espera e
      // tenta de novo até o orçamento acabar.
      await new Promise((r) => setTimeout(r, 1000));
    }
  }
  return db.isOpen();
}

/**
 * v7.47 — `versionchange`: dispara quando OUTRA aba/dispositivo abre o
 * app com uma versão de schema MAIOR e está prestes a fazer upgrade do
 * IndexedDB. Por padrão o Dexie fecha o DB automaticamente, mas SEM
 * tratamento o usuário fica preso em "DatabaseClosedError" + loop de
 * reload.
 *
 * Estratégia: fecha o DB localmente, mostra toast amigável, agenda
 * reload em 3s. A outra aba consegue completar o upgrade no intervalo.
 */
db.on('versionchange', () => {
  // eslint-disable-next-line no-console
  console.warn('[DEXIE] versionchange recebido — outra aba/dispositivo está atualizando o schema. Fechando DB local e recarregando.');
  _isShuttingDown = true;
  // v7.65 — Derruba os listeners do Firebase ANTES de fechar o DB. Sem isso,
  // callbacks onValue continuam tentando escrever no Dexie fechado (loop de
  // DatabaseClosedError). Import dinâmico evita ciclo (firebaseSync importa db).
  // v7.69 — Aguarda também as escritas de sync EM CURSO drenarem (contador)
  // antes do close — callbacks já em execução terminam sem estourar erro.
  import('@/lib/firebaseSync')
    .then(async ({ teardownFirebaseSync, waitForListenerWritesToDrain }) => {
      await teardownFirebaseSync();
      await waitForListenerWritesToDrain(2000);
    })
    .catch(() => {})
    .finally(() => { try { db.close(); } catch {} });
  // v7.97 — FIM DO RELOAD AUTOMÁTICO (bug do "duplo carregamento"): a
  // reabertura silenciosa (que já era usada nas rotas de cliente) agora é
  // UNIVERSAL. O Dexie reabre no schema novo, a sync religa e a UI recebe
  // o evento kaven:schemaUpdated — boot único e contínuo, sem refresh.
  // eslint-disable-next-line no-console
  console.warn('[DEXIE] versionchange — reabrindo em silêncio no schema novo (sem reload).');
  setTimeout(async () => {
    try { await db.open(); } catch {}
    _isShuttingDown = false;
    try {
      const { initFirebaseSync } = await import('@/lib/firebaseSync');
      await initFirebaseSync();
    } catch {}
    try { window.dispatchEvent(new CustomEvent('kaven:schemaUpdated', { detail: { source: 'versionchange' } })); } catch {}
  }, 500);
});

/**
 * v7.47 — `blocked`: dispara quando ESTA aba tenta fazer upgrade mas
 * outra aba/janela tem o DB aberto numa versão antiga e está bloqueando.
 * Sem tratamento, o open() trava indefinidamente e o app fica na splash.
 *
 * v7.83 (ticket 218940) — Antes daqui só saía um toast. O `open()` seguia
 * travado, `_isShuttingDown` continuava FALSE (então polls e writes seguiam
 * batendo num DB que não abria) e nada absorvia as rejeições: o resultado era
 * enxurrada de DatabaseClosedError e aba morta até o operador fechar todas as
 * outras na mão. Agora silencia os polls, absorve as rejeições, pede às abas
 * irmãs que liberem o DB e reabre sozinho quando o upgrade concluir.
 */
let _blockedRecovering = false;

db.on('blocked', () => {
  // eslint-disable-next-line no-console
  console.warn('[DEXIE] blocked — outra aba mantém versão antiga aberta. Coordenando liberação entre abas.');
  if (_blockedRecovering) return;
  _blockedRecovering = true;

  // Impede polls/writes de brigarem com um DB que ainda não consegue abrir.
  _isShuttingDown = true;
  const releaseSuppressor = suppressDbClosedErrors();

  try {
    import('sonner').then(({ toast }) => {
      toast.warning('Atualizando o sistema…', {
        id: 'kaven-db-blocked',
        description: 'Liberando as outras abas do Kaven para concluir a atualização.',
        duration: Infinity,
      });
    }).catch(() => {});
  } catch {}

  (async () => {
    const opened = await reopenWithRetry({ announce: true });
    if (!opened) {
      // Não abriu no orçamento. Mantém o flag e o supressor ligados de
      // propósito: com o DB fechado, liberar os polls só recriaria a
      // enxurrada. O caminho de saída é explícito, via reload.
      // eslint-disable-next-line no-console
      console.warn('[DEXIE] blocked — upgrade não concluiu no orçamento; pedindo reload explícito.');
      _blockedRecovering = false;
      try {
        const { toast } = await import('sonner');
        toast.error('Não foi possível concluir a atualização', {
          id: 'kaven-db-blocked',
          description: 'Feche as outras abas do Kaven e recarregue esta página.',
          duration: Infinity,
          action: { label: 'Recarregar', onClick: () => { try { window.location.reload(); } catch {} } },
        });
      } catch {}
      return;
    }
    _isShuttingDown = false;
    _blockedRecovering = false;
    releaseSuppressor();
    try {
      const { initFirebaseSync } = await import('@/lib/firebaseSync');
      await initFirebaseSync();
    } catch {}
    try {
      const { toast } = await import('sonner');
      toast.dismiss('kaven-db-blocked');
      toast.success('Sistema atualizado.', { duration: 2500 });
    } catch {}
    try { window.dispatchEvent(new CustomEvent('kaven:schemaUpdated', { detail: { source: 'blocked-recovered' } })); } catch {}
  })();
});

/**
 * v7.83 (ticket 218940) — Lado irmão do canal: outra aba está presa num
 * upgrade de schema e pediu o DB. Fecha com graça (derruba listeners e drena
 * writes em curso, igual ao caminho de `versionchange`) para o upgrade da
 * outra aba concluir.
 *
 * Depois de liberar, esta aba está rodando o bundle ANTIGO — reabrir no schema
 * antigo daria VersionError. Então: em rotas de cliente (/delivery, /rastreio)
 * NÃO recarrega, porque perderia carrinho/formulário em andamento (mesma
 * decisão do handler de `versionchange`); nas demais, recarrega para buscar o
 * bundle novo.
 */
let _siblingReleasing = false;

(() => {
  const ch = getDbChannel();
  if (!ch) return;
  ch.addEventListener('message', async (ev) => {
    if (ev?.data?.type !== 'release-db') return;
    if (_blockedRecovering || _siblingReleasing) return;
    _siblingReleasing = true;
    // eslint-disable-next-line no-console
    console.warn('[DEXIE] outra aba pediu liberação do DB p/ concluir upgrade de schema — fechando.');
    const releaseSuppressor = suppressDbClosedErrors();
    _isShuttingDown = true;
    try {
      const { teardownFirebaseSync, waitForListenerWritesToDrain } = await import('@/lib/firebaseSync');
      await teardownFirebaseSync();
      await waitForListenerWritesToDrain(2000);
    } catch {}
    try { db.close(); } catch {}

    const isCustomerFacingRoute = typeof window !== 'undefined' &&
      (window.location.pathname.startsWith('/delivery') || window.location.pathname.startsWith('/rastreio'));

    // Dá tempo da outra aba concluir o upgrade antes de reabrir.
    // v7.97 — SEM reload preventivo: tenta reabrir em silêncio primeiro
    // (fim do "duplo carregamento"). Reload só como último recurso, se o
    // bundle antigo não conseguir abrir o schema novo (VersionError) — e
    // nunca em rota de cliente (perderia carrinho/pagamento em andamento).
    setTimeout(async () => {
      const opened = await reopenWithRetry({ budgetMs: 20000 });
      if (opened) {
        _isShuttingDown = false;
        _siblingReleasing = false;
        try {
          const { initFirebaseSync } = await import('@/lib/firebaseSync');
          await initFirebaseSync();
        } catch {}
        try { window.dispatchEvent(new CustomEvent('kaven:schemaUpdated', { detail: { source: 'sibling-release' } })); } catch {}
        releaseSuppressor();
        return;
      }
      releaseSuppressor();
      if (!isCustomerFacingRoute) {
        // eslint-disable-next-line no-console
        console.warn('[DEXIE] bundle antigo não abre o schema novo — reload inevitável (último recurso).');
        try { window.location.reload(); } catch {}
      }
    }, 1500);
  });
})();

db.version(2).stores({
  operators: '++id, name, pin',
  mesas: '++id, number, status',
  beverages: '++id, name, category',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
});

db.version(3).stores({
  operators: '++id, name, pin',
  mesas: '++id, number, status',
  beverages: '++id, name, category',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt',
});

db.version(4).stores({
  operators: '++id, name, pin',
  mesas: '++id, number, status',
  beverages: '++id, name, category',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt',
  menuItems: '++id, name, category',
  deliveryOrders: '++id, status, customerName, createdAt',
  kdsQueue: '++id, orderId, source, status, createdAt',
});

db.version(5).stores({
  operators: '++id, name, pin',
  mesas: '++id, number, status',
  beverages: '++id, name, category',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt',
  menuItems: '++id, name, category',
  deliveryOrders: '++id, status, customerName, createdAt',
  kdsQueue: '++id, orderId, source, status, createdAt',
  loyaltyCustomers: 'phone, name',
});

// v6: Visual Menu - adds imageUrl and tags to menuItems + notifications log
db.version(6).stores({
  operators: '++id, name, pin',
  mesas: '++id, number, status',
  beverages: '++id, name, category',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt',
  menuItems: '++id, name, category',
  deliveryOrders: '++id, status, customerName, createdAt',
  kdsQueue: '++id, orderId, source, status, createdAt',
  loyaltyCustomers: 'phone, name',
  notifications: '++id, orderId, status, sentAt',
}).upgrade(async (tx) => {
  // Backfill imageUrl/tags on existing menuItems
  const items = await tx.table('menuItems').toArray();
  const patchMap = {
    'Quentinha de Carne': { imageUrl: 'https://images.unsplash.com/photo-1544025162-d76694265947?w=600&auto=format&fit=crop&q=80', tags: ['mais-pedido'] },
    'Quentinha de Frango': { imageUrl: 'https://images.unsplash.com/photo-1598103442097-8b74394b95c6?w=600&auto=format&fit=crop&q=80', tags: ['prato-do-dia'] },
    'Quentinha de Peixe': { imageUrl: 'https://images.unsplash.com/photo-1535140728325-a4d3707eee94?w=600&auto=format&fit=crop&q=80', tags: ['novo'] },
    'Marmita GG Carne': { imageUrl: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600&auto=format&fit=crop&q=80', tags: ['mais-pedido'] },
    'Marmita GG Frango': { imageUrl: 'https://images.unsplash.com/photo-1569058242261-9cb5f72e3e4f?w=600&auto=format&fit=crop&q=80', tags: [] },
    'Refrigerante 2L': { imageUrl: 'https://images.unsplash.com/photo-1629203851288-4062bb2b85a5?w=600&auto=format&fit=crop&q=80', tags: [] },
    'Suco Natural 1L': { imageUrl: 'https://images.unsplash.com/photo-1600271886742-f049cd451bba?w=600&auto=format&fit=crop&q=80', tags: ['novo', 'sugestao-chef'] },
    'Agua Mineral 500ml': { imageUrl: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=600&auto=format&fit=crop&q=80', tags: [] },
  };
  for (const it of items) {
    const patch = patchMap[it.name];
    if (patch) {
      await tx.table('menuItems').update(it.id, patch);
    } else if (!it.tags) {
      await tx.table('menuItems').update(it.id, { tags: [] });
    }
  }
});

// v7: Presets de cardápio + CRUD de bairros + tracking cross-sell (Fase 5)
db.version(7).stores({
  operators: '++id, name, pin',
  mesas: '++id, number, status',
  beverages: '++id, name, category',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt',
  menuItems: '++id, name, category',
  deliveryOrders: '++id, status, customerName, createdAt',
  kdsQueue: '++id, orderId, source, status, createdAt',
  loyaltyCustomers: 'phone, name',
  notifications: '++id, orderId, status, sentAt',
  menuPresets: '++id, name',
  neighborhoods: '++id, name, active',
  crossSellEvents: '++id, event, timestamp',
}).upgrade(async (tx) => {
  // Backfill presets: todos os menuItems ganham ['Padrão']
  const items = await tx.table('menuItems').toArray();
  for (const it of items) {
    if (!it.presets) await tx.table('menuItems').update(it.id, { presets: ['Padrão'] });
  }
});

// v8: Comandas individuais + multi-role operators (Fase 7)
db.version(8).stores({
  operators: '++id, name, pin, role',
  mesas: '++id, number, status',
  beverages: '++id, name, category',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt',
  menuItems: '++id, name, category',
  deliveryOrders: '++id, status, customerName, createdAt',
  kdsQueue: '++id, orderId, source, status, createdAt',
  loyaltyCustomers: 'phone, name',
  notifications: '++id, orderId, status, sentAt',
  menuPresets: '++id, name',
  neighborhoods: '++id, name, active',
  crossSellEvents: '++id, event, timestamp',
  commands: '++id, &number, status, createdAt, closedAt',
}).upgrade(async (tx) => {
  // Backfill role='admin' no operador 1234 existente
  const ops = await tx.table('operators').toArray();
  for (const op of ops) {
    if (!op.role) await tx.table('operators').update(op.id, { role: 'admin' });
  }
});

// v9: operators.active + isTraining flag em registros (Fase 8)
db.version(9).stores({
  operators: '++id, name, pin, role, active',
  mesas: '++id, number, status',
  beverages: '++id, name, category',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt, isTraining',
  menuItems: '++id, name, category',
  deliveryOrders: '++id, status, customerName, createdAt, isTraining',
  kdsQueue: '++id, orderId, source, status, createdAt',
  loyaltyCustomers: 'phone, name',
  notifications: '++id, orderId, status, sentAt',
  menuPresets: '++id, name',
  neighborhoods: '++id, name, active',
  crossSellEvents: '++id, event, timestamp',
  commands: '++id, &number, status, createdAt, closedAt, isTraining',
}).upgrade(async (tx) => {
  const ops = await tx.table('operators').toArray();
  for (const op of ops) {
    if (op.active === undefined) await tx.table('operators').update(op.id, { active: true });
  }
});

// v10 — Fase 17: log de auditoria local
db.version(10).stores({
  operators: '++id, name, pin, role, active',
  mesas: '++id, number, status',
  beverages: '++id, name, category',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt, isTraining',
  menuItems: '++id, name, category',
  deliveryOrders: '++id, status, customerName, createdAt, isTraining',
  kdsQueue: '++id, orderId, source, status, createdAt',
  loyaltyCustomers: 'phone, name',
  notifications: '++id, orderId, status, sentAt',
  menuPresets: '++id, name',
  neighborhoods: '++id, name, active',
  crossSellEvents: '++id, event, timestamp',
  commands: '++id, &number, status, createdAt, closedAt, isTraining',
  auditLog: '++id, at, [table+action], deviceUid',
});

// v11 — Fase 31: Sessões de caixa (abertura, fechamento, sangria, suprimento)
db.version(11).stores({
  operators: '++id, name, pin, role, active',
  mesas: '++id, number, status',
  beverages: '++id, name, category',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt, isTraining',
  menuItems: '++id, name, category',
  deliveryOrders: '++id, status, customerName, createdAt, isTraining',
  kdsQueue: '++id, orderId, source, status, createdAt',
  loyaltyCustomers: 'phone, name',
  notifications: '++id, orderId, status, sentAt',
  menuPresets: '++id, name',
  neighborhoods: '++id, name, active',
  crossSellEvents: '++id, event, timestamp',
  commands: '++id, &number, status, createdAt, closedAt, isTraining',
  auditLog: '++id, at, [table+action], deviceUid',
  cashDrawerSessions: '++id, status, openedAt, closedAt, openedBy',
});

// v12 — Fase 33: Código de barras (EAN-13) em bebidas e menuItems
db.version(12).stores({
  operators: '++id, name, pin, role, active',
  mesas: '++id, number, status',
  beverages: '++id, name, category, barcode',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt, isTraining',
  menuItems: '++id, name, category, barcode',
  deliveryOrders: '++id, status, customerName, createdAt, isTraining',
  kdsQueue: '++id, orderId, source, status, createdAt',
  loyaltyCustomers: 'phone, name',
  notifications: '++id, orderId, status, sentAt',
  menuPresets: '++id, name',
  neighborhoods: '++id, name, active',
  crossSellEvents: '++id, event, timestamp',
  commands: '++id, &number, status, createdAt, closedAt, isTraining',
  auditLog: '++id, at, [table+action], deviceUid',
  cashDrawerSessions: '++id, status, openedAt, closedAt, openedBy',
});

// v13 (v7.18) — Apaga tokens sensíveis legados de `db.config` em devices
// que gravaram tokens localmente antes da migração para o backend proxy.
// As chaves sensíveis agora vivem APENAS no Mongo `app_config`; deixá-las
// no IndexedDB é vazamento de credencial.
db.version(13).stores({
  // (schema inalterado — só usamos `upgrade` para sanitização)
  operators: '++id, name, pin, role, active',
  mesas: '++id, number, status',
  beverages: '++id, name, category, barcode',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt, isTraining',
  menuItems: '++id, name, category, barcode',
  deliveryOrders: '++id, status, customerName, createdAt, isTraining',
  kdsQueue: '++id, orderId, source, status, createdAt',
  loyaltyCustomers: 'phone, name',
  notifications: '++id, orderId, status, sentAt',
  menuPresets: '++id, name',
  neighborhoods: '++id, name, active',
  crossSellEvents: '++id, event, timestamp',
  commands: '++id, &number, status, createdAt, closedAt, isTraining',
  auditLog: '++id, at, [table+action], deviceUid',
  cashDrawerSessions: '++id, status, openedAt, closedAt, openedBy',
}).upgrade(async (tx) => {
  const LEGACY_SENSITIVE = ['zapiInstanceToken', 'zapiClientToken', 'mpAccessToken'];
  let wiped = 0;
  for (const k of LEGACY_SENSITIVE) {
    try {
      const row = await tx.table('config').get(k);
      if (row) {
        await tx.table('config').delete(k);
        wiped += 1;
      }
    } catch {/* ignora — chave pode não existir */}
  }
  // eslint-disable-next-line no-console
  console.log(`[MIGRATION v13] kavenLegacyTokenWipeV1 — ${wiped} token(s) legado(s) removido(s) do db.config`);
});

// v14 (v7.44) — Adiciona índice `customerPhone` em `deliveryOrders`.
// Motivação: o chatbot faz `db.deliveryOrders.where('customerPhone').equals(phone)`
// para localizar pedidos vindos do WhatsApp. Sem o índice, o IndexedDB faz
// full table scan; em devices com histórico longo as buscas eram lentas e,
// em algumas versões do Safari, davam "InvalidStateError" silencioso.
//
// Schema-only migration (sem `upgrade` callback): Dexie recria o índice
// automaticamente quando a versão sobe.
db.version(14).stores({
  operators: '++id, name, pin, role, active',
  mesas: '++id, number, status',
  beverages: '++id, name, category, barcode',
  orders: '++id, tableId, beverageId, createdAt',
  config: 'key',
  salesHistory: '++id, tableNumber, closedAt, isTraining',
  menuItems: '++id, name, category, barcode',
  deliveryOrders: '++id, status, customerName, customerPhone, createdAt, isTraining',
  kdsQueue: '++id, orderId, source, status, createdAt',
  loyaltyCustomers: 'phone, name',
  notifications: '++id, orderId, status, sentAt',
  menuPresets: '++id, name',
  neighborhoods: '++id, name, active',
  crossSellEvents: '++id, event, timestamp',
  commands: '++id, &number, status, createdAt, closedAt, isTraining',
  auditLog: '++id, at, [table+action], deviceUid',
  cashDrawerSessions: '++id, status, openedAt, closedAt, openedBy',
});

// v7.71 — Indice mpPaymentId em deliveryOrders/kdsQueue: necessario pro
// attachBackendOrdersListener (ponte MongoDB->Firebase) localizar/atualizar
// pedidos por mpPaymentId em vez do ID local autoIncrement (que o backend
// nao tem como gerar/prever) — ticket #207625.
db.version(15).stores({
  deliveryOrders: '++id, status, customerName, customerPhone, createdAt, isTraining, mpPaymentId',
  kdsQueue: '++id, orderId, source, status, createdAt, mpPaymentId',
});

let seedPromise = null;

export async function seedDatabase() {
  if (seedPromise) return seedPromise;
  // v7.47 — Se _doSeed REJEITAR, o `seedPromise` ficava preso na promessa
  // rejeitada para sempre — todo retry fracassava com o erro original.
  // Agora limpamos o cache no catch para permitir retry idempotente.
  seedPromise = _doSeed().catch((err) => {
    seedPromise = null;
    throw err;
  });
  return seedPromise;
}

async function _doSeed() {
  // v7.48 — Boot performance overhaul.
  //
  // ANTES: 25+ chamadas individuais de db.config.get() + db.config.put(),
  // cada uma uma transação IDB separada (~5-15ms cada). Em browsers com
  // disco lento ou IDB sob pressão, isso compõe 200ms-2s de latência só
  // pra config. Idem pra menuItems: 3 passes independentes de toArray() +
  // update() row-by-row.
  //
  // AGORA: 1 leitura única `db.config.toArray()` no início, 1 escrita única
  // `db.config.bulkPut()` no fim. Idem pra menuItems: 1 toArray + 1 bulkPut.
  // Reduz transações de ~25 → 2 em config, e de 3N → 1 em menuItems.

  // === Operadores (v7.89) ===
  // O seed local dos 5 operadores padrão foi REMOVIDO: ele recriava
  // fantasmas a cada boot (excluídos voltavam; PIN trocado fazia o seed
  // recriar o operador padrão com o PIN antigo). Agora o servidor (Mongo)
  // é a fonte única da verdade — ver lib/operatorsMirror.js.

  // === CONFIG: 1 read + N inserções/atualizações via bulkPut no final ===
  const existingConfig = await db.config.toArray();
  const configMap = new Map(existingConfig.map((c) => [c.key, c.value]));
  // `pendingWrites` acumula tudo que precisa ser bulk-put no final.
  const pendingConfigWrites = [];
  const putIfMissing = (key, value) => {
    if (!configMap.has(key)) {
      pendingConfigWrites.push({ key, value });
      configMap.set(key, value);
    }
  };
  const putAlways = (key, value) => {
    pendingConfigWrites.push({ key, value });
    configMap.set(key, value);
  };

  // Defaults básicos (config inicial — só se ausentes)
  putIfMissing('tableCount', 10);
  putIfMissing('businessName', 'Kaven Local');
  putIfMissing('buffetPricePerKg', 59.9);
  // v7.57 — Coords PRECISAS retornadas pela Places API New para o endereço
  // OFICIAL do restaurante. Google identifica o bairro como "Parque Sao Jorge"
  // (sublocality_level_1), CEP 25931-834. Endereço informado pelo cliente
  // como "Piabetá" é o bairro popular/legacy, mas o Google usa Parque Sao Jorge.
  putIfMissing('restaurantLat', -22.6118426);
  putIfMissing('restaurantLng', -43.1766153);
  putIfMissing('restaurantAddress', 'R. Artur Rodrigues Loivos, 201 - Parque Sao Jorge, Magé - RJ, 25931-834');
  // v7.57 — One-shot migration: usuários com config gravado antes da
  // correção tinham coords ERRADAS (-22.6384/-43.0945 ou -22.6116139/-43.176541
  // que apontavam pra um endereço de Rua Eduardina Miranda Telles, não o
  // endereço atual). Força overwrite para os valores corretos UMA VEZ.
  // Admin pode reajustar depois em /config se quiser.
  if (!configMap.get('restaurantCoordsV757Fixed')) {
    // eslint-disable-next-line no-console
    console.log('[MIGRATION v7.57] Corrigindo coords do restaurante para R. Artur Rodrigues Loivos, 201, Parque Sao Jorge, Magé/RJ');
    putAlways('restaurantLat', -22.6118426);
    putAlways('restaurantLng', -43.1766153);
    putAlways('restaurantAddress', 'R. Artur Rodrigues Loivos, 201 - Parque Sao Jorge, Magé - RJ, 25931-834');
    putAlways('restaurantCoordsV757Fixed', true);
  }
  putIfMissing('deliveryRadiusKm', 8.0);
  putIfMissing('deliveryBaseFee', 5.0);
  putIfMissing('deliveryPerKm', 2.0);
  putIfMissing('maxDeliveryKm', 15);
  putIfMissing('loyaltyTarget', 10);
  putIfMissing('deliveryPaused', false);
  putIfMissing('activePreset', 'Padrão');
  putIfMissing('trainingMode', false);
  putIfMissing('coffeeComboPrice', 15);
  putIfMissing('restaurantPhone', '');
  putIfMissing('restaurantGreeting', 'Olá! Preciso de ajuda com meu pedido no Kaven 🍽️');

  // Integrações (Fase 13)
  const integrationDefaults = [
    ['firebaseEnabled', false],
    ['firebaseApiKey', ''],
    ['firebaseDatabaseURL', ''],
    ['firebaseProjectId', ''],
    ['firebaseAuthDomain', ''],
    ['firebaseStorageBucket', ''],
    ['firebaseMessagingSenderId', ''],
    ['firebaseAppId', ''],
    ['mpAccessToken', ''],
    ['mpEnvironment', 'sandbox'],
    ['zapiInstanceId', ''],
    ['zapiInstanceToken', ''],
    ['zapiClientToken', ''],
  ];
  for (const [k, v] of integrationDefaults) putIfMissing(k, v);

  // v7.49 — deviceGroup CANÔNICO via backend (corrige cardápio fantasma).
  //
  // ANTES (v7.45/v7.48): cada navegador (admin + cada cliente) gerava SEU
  // próprio deviceGroup. Resultado: clientes do delivery viam um cardápio
  // ISOLADO (vazio → seed local de defaults antigos) em vez do cardápio
  // real do admin.
  //
  // AGORA: o backend é a SOURCE OF TRUTH do deviceGroup canônico.
  //
  // Ordem de precedência:
  //   1. POST /api/config/init-device-group (FIRST-WRITE-WINS)
  //      - Se backend já tem um, retorna ele → adotamos.
  //      - Se backend vazio + temos local → publicamos o local lá.
  //      - Se backend vazio + sem local → geramos um, gravamos local + publicamos.
  //   2. Fallback: dexie → localStorage → gera novo (se backend não responder).
  //
  // Side effect crítico: se o canônico DIFERE do que tínhamos em Dexie
  // (caso típico do bug atual em produção), LIMPAMOS `menuItems` local
  // pra forçar uma re-sincronização limpa do Firebase sob o novo namespace.
  // Sem isso, o cardápio fantasma persistiria no Dexie do cliente.
  const localGroup = configMap.get('deviceGroup');
  // v7.53 — Considera 'kaven-default' como deviceGroup VÁLIDO se já está
  // gravado localmente. Antes essa string era tratada como placeholder,
  // o que fazia devices nunca adotarem o canônico retornado pelo backend.
  const localGroupValid = !!localGroup;

  let canonicalGroup = null;
  try {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    if (apiUrl) {
      // v7.53 — Timeout aumentado para 10s e retry 2x. Em rede móvel 4G
      // ruim, 2s era apertado demais e o device ficava preso no UUID
      // local (causando dois aparelhos da mesma loja em namespaces
      // diferentes).
      const proposed = localGroupValid ? localGroup : (() => {
        try {
          const ls = localStorage.getItem('kaven_deviceGroup');
          if (ls) return ls;
        } catch (_) { /* localStorage indisponível */ }
        return generateUniqueDeviceGroup();
      })();
      let lastErr = null;
      for (let attempt = 0; attempt < 3 && !canonicalGroup; attempt++) {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 10000); // 10s por tentativa
        try {
          const resp = await fetch(`${apiUrl}/api/config/init-device-group`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deviceGroup: proposed }),
            signal: ctrl.signal,
          });
          clearTimeout(t);
          if (resp && resp.ok) {
            const j = await resp.json().catch(() => null);
            // v7.53 — ACEITA QUALQUER deviceGroup retornado pelo backend,
            // INCLUSIVE 'kaven-default'. Backend é a fonte da verdade
            // (env FIREBASE_DEVICE_GROUP em produção).
            if (j?.deviceGroup && typeof j.deviceGroup === 'string') {
              canonicalGroup = j.deviceGroup;
              // eslint-disable-next-line no-console
              console.log(`[MIGRATION v7.53] deviceGroup canônico = "${canonicalGroup}" (source: ${j.source}, proposed: ${proposed}, attempt: ${attempt + 1})`);
              break;
            }
          }
          lastErr = `HTTP ${resp?.status}`;
        } catch (e) {
          clearTimeout(t);
          lastErr = e?.message || String(e);
        }
        // Espera curta antes da próxima tentativa (200ms, 600ms)
        if (!canonicalGroup && attempt < 2) {
          await new Promise((r) => setTimeout(r, 200 * (attempt + 1) * (attempt + 1)));
        }
      }
      if (!canonicalGroup) {
        // eslint-disable-next-line no-console
        console.warn(`[MIGRATION v7.53] init-device-group falhou após 3 tentativas (último erro: ${lastErr})`);
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[MIGRATION v7.53] init-device-group exception:', e?.message || e);
  }

  // Se backend não respondeu, fallback para o legacy local (Dexie/localStorage/gen).
  if (!canonicalGroup) {
    canonicalGroup = localGroupValid ? localGroup : (() => {
      try {
        const ls = localStorage.getItem('kaven_deviceGroup');
        if (ls) return ls;
      } catch (_) { /* localStorage indisponível */ }
      return generateUniqueDeviceGroup();
    })();
    // eslint-disable-next-line no-console
    console.log(`[MIGRATION v7.53] deviceGroup OFFLINE-fallback = "${canonicalGroup}".`);
  }

  // Se o canônico difere do que tínhamos local, ADOTA o canônico e LIMPA
  // menuItems pra forçar re-sync limpa. Outras tabelas (deliveryOrders,
  // commands, kdsQueue) não são limpas — elas têm IDs device-of-origin
  // e o sync resolve diferenças naturalmente.
  if (localGroupValid && localGroup !== canonicalGroup) {
    // eslint-disable-next-line no-console
    console.warn(`[MIGRATION v7.53] deviceGroup MUDOU: "${localGroup}" → "${canonicalGroup}". Limpando menuItems local pra re-sync limpa.`);
    // v7.53 — Faz o clear DENTRO de uma transação Dexie + suprime
    // qualquer DatabaseClosedError que apareça como unhandledrejection
    // (o webpack-dev-server overlay reage a ele em dev mode; em prod é
    // só console). Listeners do Firebase podem estar em pleno
    // processamento de mutações na hora do clear; absorvemos a race.
    const suppressorInstall = () => {
      const handler = (ev) => {
        const reason = ev?.reason;
        const msg = reason?.message || String(reason || '');
        if (/DatabaseClosed|DexieError|MissingAPIError/i.test(msg)) {
          ev.preventDefault?.();
        }
      };
      if (typeof window !== 'undefined') {
        window.addEventListener('unhandledrejection', handler, { capture: true });
        setTimeout(() => {
          try { window.removeEventListener('unhandledrejection', handler, { capture: true }); } catch (_) { /* ignore */ }
        }, 10000);
      }
    };
    suppressorInstall();
    try {
      await db.transaction('rw', db.menuItems, async () => {
        await db.menuItems.clear();
      });
    } catch (_) { /* clear best-effort */ }
  }

  if (canonicalGroup !== localGroup) {
    putAlways('deviceGroup', canonicalGroup);
  }
  // Eco para localStorage (sempre — backup contra wipes do IDB).
  try { localStorage.setItem('kaven_deviceGroup', canonicalGroup); } catch (_) { /* ignore */ }

  // Fase 17 — Identidade do dispositivo + Modo Piloto
  putIfMissing('deviceNickname', '');
  putIfMissing('pilotMode', false);

  // Fase 29 — Etiqueta da balança
  putIfMissing('scaleBarcodePrefix', '2');
  putIfMissing('scaleBarcodePluDigits', 5);
  putIfMissing('scaleBarcodeMode', 'peso');
  putIfMissing('scaleBarcodeAutoCommand', true);

  // Fase 31.2 — requireOpenSession
  putIfMissing('requireOpenSession', true);

  // Migration v2 (idempotente via flag)
  if (!configMap.get('requireOpenSessionMigratedV2')) {
    putAlways('requireOpenSession', true);
    putAlways('requireOpenSessionMigratedV2', true);
    // eslint-disable-next-line no-console
    console.log('[MIGRATION] requireOpenSession ativado por padrão.');
  }

  // Migration Firebase prod (idempotente via flag)
  if (!configMap.get('kavenFirebaseMigratedV2')) {
    putAlways('firebaseApiKey', 'AIzaSyC_ugRNttS82VYOFaOS3W3EdSEt10yqI8o');
    putAlways('firebaseAuthDomain', 'kaven-restaurante.firebaseapp.com');
    putAlways('firebaseDatabaseURL', 'https://kaven-restaurante-default-rtdb.firebaseio.com');
    putAlways('firebaseProjectId', 'kaven-restaurante');
    putAlways('firebaseStorageBucket', 'kaven-restaurante.firebasestorage.app');
    putAlways('firebaseMessagingSenderId', '1020355003011');
    putAlways('firebaseAppId', '1:1020355003011:web:2b206445c4ad7c725651b6');
    putAlways('firebaseEnabled', true);
    putAlways('kavenFirebaseMigratedV2', true);
    // eslint-disable-next-line no-console
    console.log('[FIREBASE PROD] Credenciais reais injetadas (migration V2). Sync ativo.');
  }

  // === COMMIT config writes em UMA transação ===
  if (pendingConfigWrites.length > 0) {
    await db.config.bulkPut(pendingConfigWrites);
  }

  const firstRun = !configMap.get('firstRunSeeded');

  // v7.40 — Pre-check Firebase RTDB pra TODOS os seeds de menuItems.
  // Se já existe cardápio remoto, ESTE device é "novo" e o sync vai puxar
  // a versão real (incluindo deleções). Skipa qualquer seed local pra não
  // ressuscitar itens deletados (bug crítico do orientation técnico).
  // v7.44 — Latency tuning: timeout interno reduzido pra 1500ms + safety
  // net adicional de Promise.race(2000ms) pra garantir que o boot nunca
  // seja bloqueado mais que 2s esperando o RTDB responder.
  let skipMenuSeedsRemote = false;
  try {
    const { peekRemoteMenuItems } = await import('@/lib/firebaseSync');
    skipMenuSeedsRemote = await Promise.race([
      peekRemoteMenuItems(1500),
      new Promise((res) => setTimeout(() => res(false), 2000)),
    ]);
    if (skipMenuSeedsRemote) {
      // eslint-disable-next-line no-console
      console.log('[MIGRATION] Firebase JÁ tem menuItems remotos — TODOS os seeds locais de menuItems (v0/v3/v3.2) serão SKIPADOS pra não ressuscitar itens deletados.');
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[MIGRATION] pre-check Firebase falhou, fará seed local:', e?.message || e);
  }

  const presetCount = await db.menuPresets.count();
  if (firstRun && presetCount === 0) {
    await db.menuPresets.bulkAdd([
      { name: 'Padrão', description: 'Cardápio do dia a dia', color: '#2563eb', protected: true },
      { name: 'Fim de Semana', description: 'Sábado e domingo', color: '#F59E0B', protected: false },
      { name: 'Evento', description: 'Datas especiais e festas', color: '#E11D48', protected: false },
    ]);
  }

  // Bairros iniciais (Fase 5 - migração do lib/geo.js para Dexie)
  const nbCount = await db.neighborhoods.count();
  if (firstRun && nbCount === 0) {
    await db.neighborhoods.bulkAdd([
      { name: 'Piabeta - Centro', lat: -22.6128, lng: -43.1635, fee: null, estimatedMinutes: 25, active: true },
      { name: 'Piabeta - Vila Mariana', lat: -22.6085, lng: -43.1590, fee: null, estimatedMinutes: 30, active: true },
      { name: 'Piabeta - Parque Boneville', lat: -22.6170, lng: -43.1680, fee: null, estimatedMinutes: 30, active: true },
      { name: 'Mage - Centro', lat: -22.6534, lng: -43.1698, fee: 8.0, estimatedMinutes: 40, active: true },
      { name: 'Mage - Flexeiras', lat: -22.6410, lng: -43.1550, fee: null, estimatedMinutes: 35, active: true },
      { name: 'Inhomirim - Centro', lat: -22.5830, lng: -43.1858, fee: null, estimatedMinutes: 40, active: true },
      { name: 'Fragoso', lat: -22.5945, lng: -43.1420, fee: null, estimatedMinutes: 35, active: true },
      { name: 'Vila Inhomirim', lat: -22.5750, lng: -43.1750, fee: null, estimatedMinutes: 45, active: true },
      { name: 'Santo Aleixo', lat: -22.5180, lng: -43.1150, fee: 15.0, estimatedMinutes: 55, active: true },
      { name: 'Suruí', lat: -22.6700, lng: -43.1350, fee: null, estimatedMinutes: 40, active: true },
    ]);
  }

  const mesaCount = await db.mesas.count();
  const configTableCount = await db.config.get('tableCount');
  const targetCount = configTableCount?.value || 10;
  if (firstRun && mesaCount === 0) {
    const mesasData = [];
    for (let i = 1; i <= targetCount; i++) {
      mesasData.push({ number: i, status: 'available', total: 0, openedAt: null, openedBy: null });
    }
    await db.mesas.bulkAdd(mesasData);
  }

  const beverageCount = await db.beverages.count();
  if (firstRun && beverageCount === 0) {
    await db.beverages.bulkAdd([
      { name: 'Cerveja Lata 350ml', price: 6.5, stock: 48, category: 'Cerveja' },
      { name: 'Cerveja Long Neck 355ml', price: 8.0, stock: 24, category: 'Cerveja' },
      { name: 'Refrigerante Lata 350ml', price: 5.0, stock: 36, category: 'Refrigerante' },
      { name: 'Agua Mineral 500ml', price: 3.0, stock: 30, category: 'Agua' },
      { name: 'Suco Natural 300ml', price: 7.0, stock: 20, category: 'Suco' },
      { name: 'Energetico 250ml', price: 12.0, stock: 12, category: 'Energetico' },
      { name: 'Vodka Dose', price: 10.0, stock: 40, category: 'Destilado' },
      { name: 'Whisky Dose', price: 18.0, stock: 20, category: 'Destilado' },
    ]);
  }

  // v7.49 — Em produção (Firebase enabled), seeds locais de cardápio são
  // PROIBIDOS. Antes: se a peek do RTDB falhasse (rede ruim) ou o
  // deviceGroup estivesse desalinhado, os seeds rodavam e plantavam
  // cardápio fantasma no cliente. Agora: se Firebase está enabled, NUNCA
  // semeamos defaults — o cardápio vem 100% do Firebase. Vazio = vazio
  // (UI mostra estado de loading/empty), nunca defaults stale.
  //
  // v7.50 (2026-06-23) — REMOÇÃO TOTAL dos seeds de menuItems.
  // Mesmo com Firebase desativado, NUNCA mais semeamos pratos padrão.
  // Motivo: clientes em produção viam pratos R$ 0,00 (do KAVEN_DELIVERY_MENU)
  // por causa de seed local em devices novos antes do Firebase responder.
  // O cardápio agora é populado EXCLUSIVAMENTE pelo admin via /cardapio.
  // Para novos restaurantes, o admin cadastra os pratos manualmente.
  const firebaseEnabledNow = !!configMap.get('firebaseEnabled');
  const skipAllMenuSeeds = true; // v7.50 — sempre skipa, sem exceção.
  void skipMenuSeedsRemote; void firebaseEnabledNow; // mantidos para outros gates

  const menuCount = await db.menuItems.count();
  if (firstRun && menuCount === 0 && !skipAllMenuSeeds) {
    await db.menuItems.bulkAdd([
      { name: 'Quentinha de Carne', price: 18.0, category: 'Quentinha', description: 'Arroz, feijao, carne, salada', available: true, imageUrl: 'https://images.unsplash.com/photo-1544025162-d76694265947?w=600&auto=format&fit=crop&q=80', tags: ['mais-pedido'], presets: ['Padrão', 'Fim de Semana', 'Evento'] },
      { name: 'Quentinha de Frango', price: 16.0, category: 'Quentinha', description: 'Arroz, feijao, frango, salada', available: true, imageUrl: 'https://images.unsplash.com/photo-1598103442097-8b74394b95c6?w=600&auto=format&fit=crop&q=80', tags: ['prato-do-dia'], presets: ['Padrão', 'Fim de Semana'] },
      { name: 'Quentinha de Peixe', price: 20.0, category: 'Quentinha', description: 'Arroz, feijao, peixe, salada', available: true, imageUrl: 'https://images.unsplash.com/photo-1535140728325-a4d3707eee94?w=600&auto=format&fit=crop&q=80', tags: ['novo'], presets: ['Fim de Semana'] },
      { name: 'Marmita GG Carne', price: 25.0, category: 'Marmita', description: 'Porcao grande com carne', available: true, imageUrl: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600&auto=format&fit=crop&q=80', tags: ['mais-pedido'], presets: ['Padrão', 'Fim de Semana'] },
      { name: 'Marmita GG Frango', price: 22.0, category: 'Marmita', description: 'Porcao grande com frango', available: true, imageUrl: 'https://images.unsplash.com/photo-1569058242261-9cb5f72e3e4f?w=600&auto=format&fit=crop&q=80', tags: [], presets: ['Padrão', 'Fim de Semana'] },
      { name: 'Refrigerante 2L', price: 10.0, category: 'Bebida', description: 'Coca-Cola, Guarana ou Fanta', available: true, imageUrl: 'https://images.unsplash.com/photo-1554866585-cd94860890b7?w=600&auto=format&fit=crop&q=80', tags: [], presets: ['Padrão', 'Fim de Semana', 'Evento'] },
      { name: 'Suco Natural 1L', price: 12.0, category: 'Bebida', description: 'Laranja, Maracuja ou Acerola', available: true, imageUrl: 'https://images.unsplash.com/photo-1600271886742-f049cd451bba?w=600&auto=format&fit=crop&q=80', tags: ['novo', 'sugestao-chef'], presets: ['Padrão', 'Fim de Semana', 'Evento'] },
      { name: 'Agua Mineral 500ml', price: 3.0, category: 'Bebida', description: 'Sem gas', available: true, imageUrl: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=600&auto=format&fit=crop&q=80', tags: [], presets: ['Padrão', 'Fim de Semana', 'Evento'] },
    ]);
  }

  // v7.40 — Respeita `skipMenuSeedsRemote`.
  // v7.49 — Respeita `firebaseEnabled` (produção): cardápio vem do RTDB.
  const deliverySeed = await db.config.get('kavenDeliveryMenuSeedV1');
  if (!deliverySeed?.value && !skipAllMenuSeeds) {
    const ACOMP_EXEC = 'Acompanha mini salada fresca: alface americana, repolho roxo fininho, cenoura ralada, tomate cereja ou tomate comum em cubos, molho especial da casa (servido separado).';
    const KAVEN_DELIVERY_MENU = [
      // Executivo Premium
      { name: 'Executivo de Frango Grelhado', category: 'Executivo Premium',
        description: `Arroz, feijão, purê, legumes, frango grelhado. ${ACOMP_EXEC}` },
      { name: 'Executivo Parmegiana', category: 'Executivo Premium',
        description: `Arroz, fritas rústicas, penne ao sugo, frango à parmegiana. ${ACOMP_EXEC}` },
      { name: 'Executivo Costela Barbecue', category: 'Executivo Premium',
        description: `Arroz biro-biro, farofa, mandioca, costela suína barbecue. ${ACOMP_EXEC}` },
      { name: 'Executivo Cupim Assado', category: 'Executivo Premium',
        description: `Arroz cremoso, purê, cupim, molho madeira. ${ACOMP_EXEC}` },
      { name: 'Executivo Mineiro', category: 'Executivo Premium',
        description: `Arroz, tutu, couve, linguiça, frango com quiabo. ${ACOMP_EXEC}` },
      // Churrasco Box
      { name: 'Box Fraldinha', category: 'Churrasco Box',
        description: 'Fraldinha, arroz biro-biro, mandioca, farofa, vinagrete.' },
      { name: 'Box Costela BBQ', category: 'Churrasco Box',
        description: 'Costela, batata rústica, arroz, creme de milho.' },
      { name: 'Box Misto Kaven', category: 'Churrasco Box',
        description: 'Fraldinha, linguiça, sobrecoxa, mandioca, arroz, farofa.' },
      // Massas & Cremosos
      { name: 'Penne 4 Queijos com Frango', category: 'Massas & Cremosos', description: '' },
      { name: 'Lasanha Bolonhesa',          category: 'Massas & Cremosos', description: '' },
      { name: 'Escondidinho de Carne',      category: 'Massas & Cremosos', description: '' },
      { name: 'Fricassê de Frango',         category: 'Massas & Cremosos', description: '' },
      { name: 'Strogonoff Premium',         category: 'Massas & Cremosos', description: '' },
      // Família
      { name: 'Combo Família Churrasco',  category: 'Família', description: 'Serve 3 pessoas.', servings: 3 },
      { name: 'Combo Família Mineiro',    category: 'Família', description: '' },
      { name: 'Combo Família Parmegiana', category: 'Família', description: '' },
    ];

    const existing = await db.menuItems.toArray();
    const existingKeys = new Set(
      existing.map((it) => `${(it.name || '').trim().toLowerCase()}|${it.category || ''}`),
    );
    const toInsert = KAVEN_DELIVERY_MENU
      .filter((it) => !existingKeys.has(`${it.name.toLowerCase()}|${it.category}`))
      .map((it) => ({
        name: it.name,
        category: it.category,
        description: it.description,
        price: 0, // operador define o preço final no Admin
        servings: it.servings || null,
        imageUrl: '',
        tags: [],
        available: true,
        presets: ['Padrão'],
      }));
    if (toInsert.length > 0) {
      await db.menuItems.bulkAdd(toInsert);
    }
    await db.config.put({ key: 'kavenDeliveryMenuSeedV1', value: true });
    // eslint-disable-next-line no-console
    console.log(`[MIGRATION] Kaven Delivery menu seed v1 aplicado (${toInsert.length} novos pratos).`);
  } else if (skipMenuSeedsRemote && !deliverySeed?.value) {
    // v7.40 — Marca flag mesmo skipando: evita re-tentativa em próximos boots
    // se Firebase ficar offline depois (sync trará tudo quando reconectar).
    await db.config.put({ key: 'kavenDeliveryMenuSeedV1', value: true });
    // eslint-disable-next-line no-console
    console.log('[MIGRATION] v3 seed marcado como aplicado (skipado por presença remota).');
  }

  // Migration v3.1 (2026-02-28) — Rename de categorias para Title Case +
  // remap dos itens já existentes (preview/produção que rodaram a seed v1
  // ANTES desta correção). Também move bebidas antigas ("Bebida") para
  // "Bebidas". Idempotente via flag `kavenCategoryRenameV2`.
  //
  // Hotfix v7.1 — RENAME_MAP expandido para varrer TODAS as categorias
  // v7.48 — Migrations de menuItems COMBINADAS num único pass.
  //
  // ANTES: 3 passes independentes, cada um com `db.menuItems.toArray()`
  // (3 leituras completas) + N `update()` row-by-row (3N transações).
  //
  // AGORA: 1 `toArray()` + 1 `bulkPut()` total. Combina:
  //   (1) Rename de categorias legadas (flag `kavenCategoryRenameV2`)
  //   (2) Structural clean (flag `kavenStructuralCleanV3`)
  //   (3) Backfill de stableId (sempre roda — checagem per-item)
  const needCatRename = !configMap.get('kavenCategoryRenameV2');
  const needStructClean = !configMap.get('kavenStructuralCleanV3');
  // stableId backfill SEMPRE checa (não tem flag — é por-item).

  const RENAME_MAP = {
    'EXECUTIVOS PREMIUM': 'Executivo Premium',
    'CHURRASCO BOX': 'Churrasco Box',
    'MASSAS & CREMOSOS': 'Massas & Cremosos',
    'FAMÍLIA': 'Família',
    'Bebida': 'Bebidas',
    'Quentinha': 'Executivo Premium',
    'Quentinhas': 'Executivo Premium',
    'Marmita': 'Executivo Premium',
    'Entradas': 'Executivo Premium',
    'Carnes': 'Churrasco Box',
    'Frango': 'Executivo Premium',
    'Peixes': 'Executivo Premium',
    'Massas': 'Massas & Cremosos',
    'Saladas': 'Executivo Premium',
    'Caldos': 'Massas & Cremosos',
  };
  const VALID_CATEGORIES = new Set([
    'Executivo Premium', 'Churrasco Box', 'Massas & Cremosos', 'Família',
    'Bebidas', 'Sobremesas', 'Acompanhamentos', 'Combos', 'Outros',
  ]);
  const VALID_TAGS = new Set(['mais-pedido', 'prato-do-dia', 'novo', 'sugestao-chef']);
  const toArrSafe = (v, fb) => {
    if (Array.isArray(v)) return v;
    if (v && typeof v === 'object') return Object.values(v);
    if (typeof v === 'string' && v.trim()) return [v];
    return fb;
  };
  const genStableId = () => (typeof crypto?.randomUUID === 'function')
    ? crypto.randomUUID()
    : `mi-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

  try {
    const allMenu = await db.menuItems.toArray();
    const patches = []; // row-completo pra bulkPut (Dexie aceita update via bulkPut)
    let renamedCount = 0, cleanedCount = 0, stableIdCount = 0;

    for (const it of allMenu) {
      const patch = {};
      // (1) Rename categoria
      if (needCatRename) {
        const next = RENAME_MAP[it.category];
        if (next && next !== it.category) {
          patch.category = next;
          renamedCount += 1;
        }
      }
      // (2) Structural clean
      if (needStructClean) {
        const finalCat = patch.category ?? it.category;
        if (!VALID_CATEGORIES.has(finalCat)) {
          patch.category = 'Executivo Premium';
        }
        const ps = toArrSafe(it.presets, ['Padrão']);
        if (!Array.isArray(it.presets) || ps.length === 0) {
          patch.presets = ps.length > 0 ? ps : ['Padrão'];
        }
        const tagsArr = toArrSafe(it.tags, []).filter((t) => typeof t === 'string' && VALID_TAGS.has(t));
        const origIsArr = Array.isArray(it.tags);
        const origLen = origIsArr ? it.tags.length : (it.tags && typeof it.tags === 'object' ? Object.keys(it.tags).length : 0);
        if (!origIsArr || origLen !== tagsArr.length) {
          patch.tags = tagsArr;
        }
        if (it.available === undefined || it.available === null) {
          patch.available = true;
        }
        if (typeof it.servings === 'number' && it.servings <= 0) {
          patch.servings = null;
        }
        if (Object.keys(patch).length > 0) cleanedCount += 1;
      }
      // (3) stableId backfill (sempre)
      if (!it.stableId) {
        patch.stableId = genStableId();
        stableIdCount += 1;
      }
      if (Object.keys(patch).length > 0) {
        patches.push({ ...it, ...patch });
      }
    }

    if (patches.length > 0) {
      await db.menuItems.bulkPut(patches);
    }
    // Marca flags só se as passes correspondentes estavam pendentes (pendingConfigWrites
    // será committed mais abaixo num único bulkPut final).
    if (needCatRename) {
      pendingConfigWrites.push({ key: 'kavenCategoryRenameV2', value: true });
      configMap.set('kavenCategoryRenameV2', true);
      // eslint-disable-next-line no-console
      console.log(`[MIGRATION v7.48 unified] kavenCategoryRenameV2 — ${renamedCount} itens recategorizados.`);
    }
    if (needStructClean) {
      pendingConfigWrites.push({ key: 'kavenStructuralCleanV3', value: true });
      configMap.set('kavenStructuralCleanV3', true);
      // eslint-disable-next-line no-console
      console.log(`[MIGRATION v7.48 unified] kavenStructuralCleanV3 — ${cleanedCount} itens limpos.`);
    }
    if (stableIdCount > 0) {
      // eslint-disable-next-line no-console
      console.log(`[MIGRATION v7.48 unified] stableId — ${stableIdCount} itens ganharam UUID estável.`);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[MIGRATION v7.48 unified] menuItems pass falhou:', e?.message || e);
  }

  // v7.45-legacy — Mantém o block original substituído desativado. O legacy
  // block abaixo (com toArray + update por item) FOI MOVIDO PRA ACIMA.
  if (false) {
    const RENAME_MAP = {
      // CAPS LOCK (versão intermediária que esteve no preview/produção)
      'EXECUTIVOS PREMIUM': 'Executivo Premium',
      'CHURRASCO BOX': 'Churrasco Box',
      'MASSAS & CREMOSOS': 'Massas & Cremosos',
      'FAMÍLIA': 'Família',
      // Categorias legadas do seed v0 (Bebida, Quentinha, Marmita, etc)
      'Bebida': 'Bebidas',
      'Quentinha': 'Executivo Premium',  // fallback
      'Quentinhas': 'Executivo Premium', // fallback
      'Marmita': 'Executivo Premium',    // fallback
      'Entradas': 'Executivo Premium',
      'Carnes': 'Churrasco Box',
      'Frango': 'Executivo Premium',
      'Peixes': 'Executivo Premium',
      'Massas': 'Massas & Cremosos',
      'Saladas': 'Executivo Premium',
      'Caldos': 'Massas & Cremosos',
    };
    const all = await db.menuItems.toArray().catch(() => []);
    let updated = 0;
    for (const it of all) {
      const next = RENAME_MAP[it.category];
      if (next && next !== it.category) {
        try {
          await db.menuItems.update(it.id, { category: next });
          updated += 1;
        } catch (uerr) {
          // eslint-disable-next-line no-console
          console.warn(`[MIGRATION] rename pulou item #${it.id}:`, uerr?.message || uerr);
        }
      }
    }
    await db.config.put({ key: 'kavenCategoryRenameV2', value: true });
    // eslint-disable-next-line no-console
    console.log(`[MIGRATION] kavenCategoryRenameV2 aplicado — ${updated} itens recategorizados.`);
  }

  // Migration v3.2 (2026-02-28) — Seed mínimo da categoria Bebidas no
  // cardápio do delivery. Idempotente via `kavenBeveragesMenuSeedV1`.
  // Dedup por (name|category): se já existir uma "Coca-Cola Lata" em
  // Bebidas, pula. Operador define preço final no Admin.
  //
  // v7.40 — Respeita `skipMenuSeedsRemote`: se o Firebase tem cardápio,
  // não ressuscita bebidas deletadas pelo admin.
  // v7.49 — Respeita firebaseEnabled (produção): cardápio vem do RTDB.
  const bevMenuSeed = await db.config.get('kavenBeveragesMenuSeedV1');
  if (!bevMenuSeed?.value && !skipAllMenuSeeds) {
    const KAVEN_BEVERAGES_MENU = [
      { name: 'Coca-Cola Lata 350ml',       description: 'Refrigerante gelado.' },
      { name: 'Guaraná Antarctica 350ml',   description: 'Refrigerante gelado.' },
      { name: 'Coca-Cola 2L',               description: 'Refrigerante 2 litros.' },
      { name: 'Suco Natural 500ml',         description: 'Laranja, maracujá ou acerola.' },
      { name: 'Água Mineral 500ml',         description: 'Sem gás.' },
    ];
    const existingMenu = await db.menuItems.toArray();
    const existingKeysBev = new Set(
      existingMenu.map((it) => `${(it.name || '').trim().toLowerCase()}|${it.category || ''}`),
    );
    const toInsertBev = KAVEN_BEVERAGES_MENU
      .filter((it) => !existingKeysBev.has(`${it.name.toLowerCase()}|Bebidas`))
      .map((it) => ({
        name: it.name,
        category: 'Bebidas',
        description: it.description,
        price: 0,
        servings: null,
        imageUrl: '',
        tags: [],
        available: true,
        presets: ['Padrão'],
      }));
    if (toInsertBev.length > 0) {
      await db.menuItems.bulkAdd(toInsertBev);
    }
    await db.config.put({ key: 'kavenBeveragesMenuSeedV1', value: true });
    // eslint-disable-next-line no-console
    console.log(`[MIGRATION] kavenBeveragesMenuSeedV1 aplicado — ${toInsertBev.length} bebidas adicionadas.`);
  } else if (skipMenuSeedsRemote && !bevMenuSeed?.value) {
    await db.config.put({ key: 'kavenBeveragesMenuSeedV1', value: true });
    // eslint-disable-next-line no-console
    console.log('[MIGRATION] v3.2 seed marcado como aplicado (skipado por presença remota).');
  }

  // Migration v3.3 (2026-02-29) — Limpeza estrutural agressiva.
  // Garante que TODO item em `db.menuItems` está bem-formado para o
  // sistema atual (5 categorias oficiais + presets/tags como array).
  //
  // Fix aplicado por item:
  //   1. `category` fora das 5 oficiais → fallback `Executivo Premium`.
  //   2. `presets` ausente, objeto-Firebase ou tipo errado → `['Padrão']`.
  //   3. `tags` ausente, objeto-Firebase ou tipo errado → `[]`.
  //   4. `tags` com valor que não está em TAG_OPTIONS válidas → removida.
  //   5. `available` undefined/null → `true`.
  //   6. `servings` 0 ou negativo → `null` (para o badge não renderizar lixo).
  //
  // Idempotente via flag `kavenStructuralCleanV3`. Cada update é
  // isolado em try/catch.
  // v7.48 — DESATIVADO: lógica movida pra unified pass acima.
  const structClean = await db.config.get('kavenStructuralCleanV3');
  if (false && !structClean?.value) {
    const VALID_CATEGORIES = new Set([
      'Executivo Premium',
      'Churrasco Box',
      'Massas & Cremosos',
      'Família',
      'Bebidas',
    ]);
    const VALID_TAGS = new Set(['mais-pedido', 'prato-do-dia', 'novo', 'sugestao-chef']);
    const toArr = (v, fb) => {
      if (Array.isArray(v)) return v;
      if (v && typeof v === 'object') return Object.values(v);
      if (typeof v === 'string' && v.trim()) return [v];
      return fb;
    };
    const allMenu = await db.menuItems.toArray().catch(() => []);
    let fixed = 0;
    for (const it of allMenu) {
      const patch = {};
      // (1) categoria
      if (!VALID_CATEGORIES.has(it.category)) {
        patch.category = 'Executivo Premium';
      }
      // (2) presets
      const ps = toArr(it.presets, ['Padrão']);
      if (!Array.isArray(it.presets) || ps.length === 0) {
        patch.presets = ps.length > 0 ? ps : ['Padrão'];
      }
      // (3)+(4) tags
      const tagsArr = toArr(it.tags, []).filter((t) => typeof t === 'string' && VALID_TAGS.has(t));
      const originalTagsIsArray = Array.isArray(it.tags);
      const originalLen = originalTagsIsArray ? it.tags.length : (it.tags && typeof it.tags === 'object' ? Object.keys(it.tags).length : 0);
      if (!originalTagsIsArray || originalLen !== tagsArr.length) {
        patch.tags = tagsArr;
      }
      // (5) available
      if (it.available === undefined || it.available === null) {
        patch.available = true;
      }
      // (6) servings
      if (typeof it.servings === 'number' && it.servings <= 0) {
        patch.servings = null;
      }
      if (Object.keys(patch).length > 0) {
        try {
          await db.menuItems.update(it.id, patch);
          fixed += 1;
        } catch (uerr) {
          // eslint-disable-next-line no-console
          console.warn(`[MIGRATION] structural-clean pulou item #${it.id}:`, uerr?.message || uerr);
        }
      }
    }
    await db.config.put({ key: 'kavenStructuralCleanV3', value: true });
    // eslint-disable-next-line no-console
    console.log(`[MIGRATION] kavenStructuralCleanV3 aplicado — ${fixed}/${allMenu.length} itens sanitizados.`);
  }

  // Marca primeira execução como concluída — bloqueia re-seed em logins futuros.
  // Limpa apenas via resetDatabase() (que deleta o IndexedDB inteiro).
  if (firstRun) {
    await db.config.put({ key: 'firstRunSeeded', value: true });
  }

  // v7.18 — Safety net idempotente: garante que tokens sensíveis legados
  // foram removidos do db.config mesmo se a `upgrade` da v13 não rodou
  // (ex.: device que já estava na v13 antes desta release).
  const legacyWipe = await db.config.get('kavenLegacyTokenWipeV1');
  if (!legacyWipe?.value) {
    const LEGACY_SENSITIVE = ['zapiInstanceToken', 'zapiClientToken', 'mpAccessToken'];
    let wiped = 0;
    for (const k of LEGACY_SENSITIVE) {
      try {
        const row = await db.config.get(k);
        if (row) {
          await db.config.delete(k);
          wiped += 1;
        }
      } catch {/* ignora */}
    }
    await db.config.put({ key: 'kavenLegacyTokenWipeV1', value: true });
    // eslint-disable-next-line no-console
    console.log(`[MIGRATION] kavenLegacyTokenWipeV1 safety-net — ${wiped} token(s) legado(s) removido(s).`);
  }

  // v7.48 — Backfill de stableId MOVIDO para o unified pass acima.
  // Bloco legado mantido como NO-OP para histórico de migrations.
  // (Removido: db.menuItems.toArray() + update() row-by-row.)

  // === v7.51 — AUTO-PURGE de pratos fantasma em todo boot ===
  // Defesa última: independente de tombstones/stableId/sync, se qualquer
  // item no Dexie local bater na heurística `isGhostMenuItem`, ele é
  // deletado agora. Também grava tombstones com stableId E numericId E
  // nome para propagar a exclusão para todos os outros devices via
  // Firebase __tombstones__ → flushTombstones em todos os browsers.
  //
  // Roda em <50ms para um menu típico de 30-50 itens, então não afeta
  // o boot time.
  try {
    const { isGhostMenuItem, GHOST_NAMES } = await import('./lib/ghostMenuBlacklist.js');
    const { addTombstone } = await import('./lib/tombstones.js');
    const allItems = await db.menuItems.toArray();
    const ghosts = allItems.filter(isGhostMenuItem);
    if (ghosts.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(`[GHOST-PURGE v7.51] ${ghosts.length} prato(s) fantasma detectado(s) no boot — purgando: ${ghosts.map((g) => g.name).join(', ')}`);
      const idsToDelete = ghosts.map((g) => g.id).filter((id) => Number.isFinite(id));
      try { await db.menuItems.bulkDelete(idsToDelete); } catch {}
      // Tombstones por stableId E nome (defesa redundante)
      for (const g of ghosts) {
        try {
          await addTombstone('menuItems', {
            stableId: g.stableId || null,
            id: g.id,
            name: g.name,
            reason: 'ghost_auto_purge_v7.51',
          });
        } catch {}
      }
    }
    // Também tombstone qualquer nome que esteja em GHOST_NAMES, mesmo sem
    // o item presente no Dexie atual. Isso evita ressurreição via sync
    // tardio de devices offline.
    try {
      const tombsKey = 'deletedTombstones__menuItems__byName';
      const existing = (await db.config.get(tombsKey))?.value || [];
      const known = new Set(existing);
      let added = 0;
      for (const name of GHOST_NAMES) {
        if (!known.has(name)) { known.add(name); added++; }
      }
      if (added > 0) {
        await db.config.put({ key: tombsKey, value: Array.from(known) });
      }
    } catch {}
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[GHOST-PURGE v7.51] falhou:', e?.message || e);
  }

  // === COMMIT final de TODAS as flags de migração pendentes em UMA transação ===
  // v7.48 — Em vez de cada flag fazer seu próprio `db.config.put(...)` em
  // série, acumulamos tudo em `pendingConfigWrites` e flushamos aqui.
  if (pendingConfigWrites.length > 0) {
    // Filtra duplicatas (mantém o último por key) — bulkPut já lida com
    // duplicates internamente, mas filtrar reduz payload.
    const finalMap = new Map();
    for (const e of pendingConfigWrites) finalMap.set(e.key, e);
    await db.config.bulkPut(Array.from(finalMap.values()));
  }
}

export const LOYALTY_TARGET = 10;

export async function getLoyalty(phone) {
  if (!phone) return { orderCount: 0, name: '' };
  const record = await db.loyaltyCustomers.get(phone);
  return record || { phone, orderCount: 0, name: '' };
}

export async function incrementLoyalty(phone, name) {
  const existing = await db.loyaltyCustomers.get(phone);
  if (existing) {
    const newCount = (existing.orderCount || 0) + 1;
    await db.loyaltyCustomers.update(phone, { orderCount: newCount, name });
    return newCount;
  }
  await db.loyaltyCustomers.put({ phone, name, orderCount: 1 });
  return 1;
}

export async function resetLoyalty(phone) {
  await db.loyaltyCustomers.update(phone, { orderCount: 0 });
}

/**
 * v7.47 — Reset seguro do banco local.
 *
 * Antes: `db.delete()` + `db.open()` direto. Problemas:
 *   1. Listeners Firebase continuavam escrevendo no DB ENQUANTO ele
 *      estava sendo deletado → `DatabaseClosedError` em writes → fila
 *      `pendingWrites` corrompida (refs a IDs do schema antigo).
 *   2. `seedPromise` antigo apontava pra um DB já morto, próximo
 *      `seedDatabase()` falhava silenciosamente.
 *   3. App pulava entre splash → DatabaseClosedError → splash em loop.
 *
 * Agora:
 *   1. Marca `_isShuttingDown = true` → polls/writes param de tentar reabrir.
 *   2. Tear-down dos listeners Firebase (`teardownFirebaseSync`).
 *   3. Espera in-flight flush drenar (poll de 300ms até 2s timeout).
 *   4. Limpa fila `pendingWrites`.
 *   5. Fecha DB com graça (`db.close()`), só então `db.delete()`.
 *   6. `db.open()` e re-libera o flag.
 */
export async function resetDatabase() {
  _isShuttingDown = true;
  seedPromise = null;

  // Passo 2: derruba Firebase listeners + auth.
  try {
    const { teardownFirebaseSync, getPendingWritesStatus, clearPendingWrites, waitForListenerWritesToDrain } =
      await import('@/lib/firebaseSync');
    try { await teardownFirebaseSync(); } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[RESET] teardownFirebaseSync falhou (segue):', e?.message || e);
    }

    // v7.69 — Aguarda escritas de sync EM CURSO (callbacks onValue já em
    // execução) drenarem antes de fechar. Evita DatabaseClosedError em massa.
    try { await waitForListenerWritesToDrain(2000); } catch {}

    // Passo 3: drena flush em curso. Espera no máximo 2s.
    const start = Date.now();
    while (Date.now() - start < 2000) {
      const { size, flushing } = getPendingWritesStatus();
      if (size === 0 && !flushing) break;
      await new Promise((r) => setTimeout(r, 300));
    }

    // Passo 4: limpa fila (qualquer write não-flushado é descartado —
    // os IDs vão deixar de existir após delete()).
    try { clearPendingWrites(); } catch {}
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[RESET] não consegui importar firebaseSync (segue mesmo assim):', e?.message || e);
  }

  // Passo 5+6: fecha → deleta → reabre.
  // v7.66 — finally garante que o flag NUNCA fica preso em true, mesmo se
  // delete()/open() lançarem (ex.: outro device segurando o DB aberto).
  // Flag preso = attachTableListener/useVisiblePolling silenciam toda a
  // sync de entrada (Cozinha → Entrega parava de sincronizar).
  try {
    try { db.close(); } catch {}
    await db.delete();
    await db.open();
  } finally {
    _isShuttingDown = false;
  }
}

export default db;
